import React, { useEffect, useRef, useState } from 'react';
import { IFramework } from '@/framework';
import { base64Kit } from '@/utils/base64-kit';
import { IBackgroundLayerProps, ILayerProps } from '@/version-types';
import bgImage from '../assets/bg.png';
import './crop-background-operate.less';

export interface ICropImageOperateProps {
    scale: number;
    layer: ILayerProps;
    stageWidth: number;
    stageHeight: number;
    framework: IFramework;
}

const BaseLayer: React.FC<ICropImageOperateProps> = ({
    layer: activeLayer,
    scale,
    stageWidth,
    stageHeight,
    framework,
}) => {
    const imgCache = useRef<any>({});
    const {
        url,
        imageTransform: { scale: imgScale, flipV, flipH, widthRatio, heightRatio, offsetX, offsetY, rotate: imgRotate },
    } = activeLayer.data as IBackgroundLayerProps;
    const [rotate, setRotate] = useState({
        rotateX: 0,
        rotateY: 0,
        rotateZ: 0,
    });
    const [ratio, setRatio] = useState({ widthRatio: 1, heightRatio: 1 });
    const [src, setSrc] = useState('');
    // 拖动相关属性
    const [dragBg, setDragBg] = useState({ x: 0, y: 0 });
    const drag = useRef<any>({
        imgX: 0,
        imgY: 0,
        isMove: false,
        startPos: { x: 0, y: 0 },
        movePos: { x: 0, y: 0 },
        layer: {},
        imgMaxX: 0,
        imgMaxY: 0,
        imgScale: 0,
        scale: 0,
    });

    const startMouse = (e: MouseEvent) => {
        const { pageX, pageY } = e;
        drag.current.isMove = true;
        // drag.current.startPos = windowToCanvas(pageX, pageY);
        drag.current.startPos = { x: pageX, y: pageY };
        (document.querySelector('.l-crop-operate-background') as any).style.cursor = 'move';
    };
    const moveMouse = (e: MouseEvent) => {
        if (!drag.current.isMove) return false;
        const { pageX, pageY } = e;
        // drag.current.movePos = windowToCanvas(pageX, pageY);
        drag.current.movePos = { x: pageX, y: pageY };
        const {
            current: { movePos, startPos, imgScale, scale },
        } = drag;
        const directionX = flipH ? -1 : 1;
        const directionY = flipV ? -1 : 1;
        const x = movePos.x - startPos.x,
            y = movePos.y - startPos.y;
        drag.current.imgX += x * directionX;
        drag.current.imgY += y * directionY;
        setDragBg({
            x: drag.current.imgX,
            y: drag.current.imgY,
        });
        drag.current.startPos = { ...movePos }; // 更新最新位置
    };
    const drawImage = () => {
        const data = drag.current.layer.data as IBackgroundLayerProps;
        let { imgX, imgMaxX, imgY, imgMaxY, imgScale, scale } = drag.current;
        imgMaxX = imgMaxX * scale;
        imgMaxY = imgMaxY * scale;
        const xUnit = imgX < 0 ? -1 : 1;
        const yUnit = imgY < 0 ? -1 : 1;
        const x = Math.abs(imgX) > imgMaxX ? imgMaxX * xUnit : imgX;
        const y = Math.abs(imgY) > imgMaxY ? imgMaxY * yUnit : imgY;
        console.log(imgX, imgMaxX, imgY, imgMaxY);
        data.imageTransform.offsetX = x / stageWidth / widthRatio / scale / imgScale;
        data.imageTransform.offsetY = y / stageHeight / heightRatio / scale / imgScale;
        framework.stage.addOrUpdate({ ...drag.current.layer });
    };
    const endMouse = () => {
        drag.current.isMove = false;
        (document.querySelector('.l-crop-operate-background') as any).style.cursor = '';
        drawImage();
    };
    useEffect(() => {
        const fn = async () => {
            const src = imgCache.current[url] || base64Kit.getLocalUrl(url);
            imgCache.current[url] = src;
            drag.current.imgX = offsetX * imgScale * stageWidth * widthRatio * scale;
            drag.current.imgY = offsetY * imgScale * stageHeight * heightRatio * scale;
            drag.current.imgMaxX = (stageWidth * widthRatio * imgScale - stageWidth) / 2;
            drag.current.imgMaxY = (stageHeight * heightRatio * imgScale - stageHeight) / 2;
            drag.current.imgScale = imgScale;
            drag.current.layer = activeLayer;
            drag.current.scale = scale;
            setDragBg({
                x: drag.current.imgX,
                y: drag.current.imgY,
            });
            setSrc(src);
            rotate.rotateX = flipV ? 180 : 0;
            rotate.rotateY = flipH ? 180 : 0;
            setRotate(rotate);
            setRatio({ widthRatio, heightRatio });
        };
        fn();
    }, [activeLayer, scale]);

    useEffect(() => {
        if (!src) return;
        const div = document.querySelector(`#crop-bg-${activeLayer.id}`) as any;
        const container = document.querySelector(`.l-crop-operate-background`) as any;
        div?.addEventListener('mousedown', startMouse, true);
        container?.addEventListener('mousemove', moveMouse, true);
        container?.addEventListener('mouseup', endMouse, true);
        (document.querySelector('.editor-element__scroll-container') as any).style.visibility = 'hidden';
        return () => {
            div?.removeEventListener('mousedown', startMouse);
            container?.removeEventListener('mousemove', moveMouse);
            container?.removeEventListener('mouseup', endMouse);
            (document.querySelector('.editor-element__scroll-container') as any).style.visibility = 'visible';
        };
    }, [src]);
    if (!src) {
        return null;
    }

    return (
        <>
            <div
                id={`crop-bg-${activeLayer.id}`}
                style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: `translate(-50%, -50%)  rotateX(${rotate.rotateX}deg) rotateY(${rotate.rotateY}deg)`,
                    width: `${stageWidth * scale}px`,
                    height: `${stageHeight * scale}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    zIndex: 1,
                    backgroundImage: `url(${bgImage})`,
                    backgroundRepeat: 'repeat',
                    cursor: 'move',
                }}
            >
                <img
                    src={src}
                    alt=""
                    style={{
                        width: ratio.widthRatio * stageWidth * scale + 'px',
                        height: ratio.heightRatio * stageHeight * scale + 'px',
                        transform: `translateX(${dragBg.x}px) translateY(${dragBg.y}px) rotateZ(${rotate.rotateZ}deg) scale(${imgScale}) rotate(${imgRotate}deg)`,
                        pointerEvents: 'none',
                        flexShrink: 0,
                    }}
                />
            </div>
            {/* 蒙层 */}
            <div
                style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: `translate(-50%, -50%)  rotateX(${rotate.rotateX}deg) rotateY(${rotate.rotateY}deg)`,
                    width: `${stageWidth * scale}px`,
                    height: `${stageHeight * scale}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: -1,
                }}
            >
                <img
                    src={src}
                    alt=""
                    style={{
                        width: ratio.widthRatio * stageWidth * scale + 'px',
                        height: ratio.heightRatio * stageHeight * scale + 'px',
                        transform: `translateX(${dragBg.x}px) translateY(${dragBg.y}px) rotateZ(${rotate.rotateZ}deg) scale(${imgScale}) rotate(${imgRotate}deg)`,
                        opacity: 0.3,
                        pointerEvents: 'none',
                        flexShrink: 0,
                    }}
                />
            </div>
        </>
    );
};

const CropBackgroundOperate: React.FC<ICropImageOperateProps> = ({
    scale,
    layer,
    stageWidth = 100,
    stageHeight = 100,
    framework,
}) => {
    return (
        <div className="l-crop-operate-background">
            <BaseLayer
                layer={layer}
                scale={scale}
                stageWidth={stageWidth}
                stageHeight={stageHeight}
                framework={framework}
            ></BaseLayer>
        </div>
    );
};

export default CropBackgroundOperate;
